const COLOR_INPUT = 'variant-picker[data-template-product-match="true"] input[type="radio"][data-mahjwell-preview-url]';
const galleryCache = new Map();
const requestCache = new Map();
const warmedUrls = new Set();

function parseSrcset(srcset = '') {
  return srcset
    .split(',')
    .map((item) => {
      const parts = item.trim().split(/\s+/);
      const width = Number.parseInt(parts[1] || '0', 10);
      return { url: parts[0], width: Number.isNaN(width) ? 0 : width };
    })
    .filter((item) => item.url);
}

function chooseCandidate(img, targetWidth) {
  const candidates = parseSrcset(img.getAttribute('srcset') || '').sort((a, b) => a.width - b.width);
  if (!candidates.length) return img.getAttribute('src') || '';

  const atLeast = candidates.find((candidate) => candidate.width >= targetWidth);
  return (atLeast || candidates[candidates.length - 1]).url;
}

function warm(url) {
  if (!url || warmedUrls.has(url)) return;
  warmedUrls.add(url);
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
}

function optimizeCachedGallery(gallery) {
  const clone = gallery.cloneNode(true);
  const mainImages = Array.from(clone.querySelectorAll('slideshow-slide .product-media__image'));
  const thumbnailImages = Array.from(
    clone.querySelectorAll('slideshow-controls[thumbnails] img, .dialog-thumbnails-list img')
  );

  // Keep the instant cached gallery intentionally lightweight.
  mainImages.forEach((img, index) => {
    const url = chooseCandidate(img, index === 0 ? 832 : 640);
    if (!url) return;
    img.removeAttribute('srcset');
    img.setAttribute('src', url);
    img.setAttribute('loading', index === 0 ? 'eager' : 'lazy');
    img.setAttribute('fetchpriority', index === 0 ? 'high' : 'auto');
    if (index === 0) warm(url);
  });

  thumbnailImages.forEach((img) => {
    const url = chooseCandidate(img, 240);
    if (!url) return;
    img.removeAttribute('srcset');
    img.setAttribute('src', url);
    img.setAttribute('loading', 'eager');
    warm(url);
  });

  return clone.outerHTML;
}

function combinationForInput(picker, input) {
  const fieldsets = Array.from(picker.querySelectorAll('fieldset[data-fieldset-index]'));
  if (!fieldsets.length) return [];

  return fieldsets.map((fieldset) => {
    const index = Number.parseInt(fieldset.dataset.fieldsetIndex || '-1', 10);
    if (index === Number.parseInt(input.dataset.fieldsetIndex || '-2', 10)) {
      return input.dataset.optionValueId || '';
    }
    return fieldset.querySelector('input:checked')?.dataset?.optionValueId || '';
  }).filter(Boolean);
}

function buildPrefetchUrl(picker, input) {
  const productUrl = (picker.dataset.productUrl || '').split('?')[0];
  const sectionId = picker.dataset.sectionId;
  const optionValues = combinationForInput(picker, input);
  if (!productUrl || !sectionId || !optionValues.length) return '';

  const url = new URL(productUrl, window.location.origin);
  url.searchParams.set('section_id', sectionId);
  url.searchParams.set('option_values', optionValues.join(','));
  return url.toString();
}

function cacheKey(input) {
  return input.dataset.variantId || input.dataset.optionValueId || input.value;
}

async function prefetchInput(input) {
  const picker = input.closest('variant-picker');
  if (!picker) return;

  const key = cacheKey(input);
  if (!key || galleryCache.has(key)) return;

  const url = buildPrefetchUrl(picker, input);
  if (!url) return;

  if (requestCache.has(url)) {
    const html = await requestCache.get(url);
    if (html) galleryCache.set(key, html);
    return;
  }

  const request = fetch(url, { credentials: 'same-origin' })
    .then((response) => {
      if (!response.ok) throw new Error(`Variant gallery prefetch failed: ${response.status}`);
      return response.text();
    })
    .then((text) => {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const gallery = doc.querySelector('media-gallery');
      return gallery ? optimizeCachedGallery(gallery) : '';
    })
    .catch(() => '');

  requestCache.set(url, request);
  const html = await request;
  if (html) galleryCache.set(key, html);
}

function replaceWithCachedGallery(input) {
  const html = galleryCache.get(cacheKey(input));
  if (!html) return false;

  const section = input.closest('.shopify-section') || document;
  const current = section.querySelector('media-gallery');
  if (!current) return false;

  const template = document.createElement('template');
  template.innerHTML = html.trim();
  const next = template.content.querySelector('media-gallery');
  if (!next) return false;

  current.replaceWith(next);
  return true;
}

function showFirstImageFallback(input) {
  const url = input?.dataset?.mahjwellPreviewUrl;
  if (!url) return;

  const section = input.closest('.shopify-section') || document;
  const gallery = section.querySelector('media-gallery');
  const image =
    gallery?.querySelector('slideshow-slide[aria-hidden="false"] .product-media__image') ||
    gallery?.querySelector('slideshow-slide .product-media__image') ||
    gallery?.querySelector('.product-media__image');

  if (!image) return;
  image.removeAttribute('srcset');
  image.src = url;
}

function activate(input) {
  if (!replaceWithCachedGallery(input)) {
    showFirstImageFallback(input);
    // If background prefetch has not finished yet, start/raise this one immediately.
    prefetchInput(input);
  }
}

function scheduleAllPrefetches() {
  const inputs = Array.from(document.querySelectorAll(COLOR_INPUT));
  if (!inputs.length) return;

  // Start all variant gallery requests immediately; stagger only the JS scheduling
  // so parsing several responses does not block first paint.
  inputs.forEach((input, index) => {
    if (input.checked) return;
    setTimeout(() => prefetchInput(input), Math.min(index * 35, 180));
  });
}

if (!window.__mahjwellInstantVariantGalleryBound) {
  window.__mahjwellInstantVariantGalleryBound = true;

  document.addEventListener(
    'pointerenter',
    (event) => {
      const label = event.target instanceof Element
        ? event.target.closest('.variant-option__button-label--has-swatch')
        : null;
      const input = label?.querySelector(COLOR_INPUT);
      if (input && !input.checked) prefetchInput(input);
    },
    true
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      const label = event.target instanceof Element
        ? event.target.closest('.variant-option__button-label--has-swatch')
        : null;
      const input = label?.querySelector(COLOR_INPUT);
      if (input && !input.checked) activate(input);
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target instanceof Element ? event.target.closest(COLOR_INPUT) : null;
      if (input) activate(input);

      // Variant picker markup can morph after the server response. Rewarm any new IDs.
      setTimeout(scheduleAllPrefetches, 50);
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleAllPrefetches, { once: true });
  } else {
    scheduleAllPrefetches();
  }
}
